import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import { ANNUAL_PLAN } from "../../../utils/readingPlan";

interface ReadingPlanViewProps {
  onNavigate: (bookId: number, chapter: number) => void;
}

const PROGRESS_KEY = (userId: string) => `reading_progress_${userId}`;

function SyncCodePromptModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-[#111] border border-[#2e2e2e] rounded-2xl p-8 w-full max-w-sm text-center">
        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-[#ef4444]/10 rounded-full">
          <i className="ri-smartphone-line text-[#ef4444] text-xl" />
        </div>
        <h3 className="text-white text-lg font-semibold mb-2">Save Your Progress</h3>
        <p className="text-[#666] text-sm mb-6 leading-relaxed">
          Get a sync code to save your reading progress and transfer it across devices.
        </p>
        <div className="flex flex-col gap-3">
          <Link
            to="/settings"
            className="w-full bg-[#ef4444] hover:bg-[#dc2626] text-white text-sm font-medium py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Get My Sync Code
          </Link>
          <button
            onClick={onClose}
            className="text-[#444] hover:text-[#666] text-sm transition-colors cursor-pointer whitespace-nowrap mt-1"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ReadingPlanView({ onNavigate }: ReadingPlanViewProps) {
  const { user } = useAuth();
  const [progress, setProgress] = useState<Record<number, boolean>>({});
  const [showAll, setShowAll] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    if (!user) {
      setProgress({});
      return;
    }
    try {
      const stored = localStorage.getItem(PROGRESS_KEY(user.id));
      if (stored) {
        const days: number[] = JSON.parse(stored);
        const map: Record<number, boolean> = {};
        days.forEach((d) => { map[d] = true; });
        setProgress(map);
      } else {
        setProgress({});
      }
    } catch {
      setProgress({});
    }
  }, [user]);

  const saveProgress = useCallback((updatedProgress: Record<number, boolean>) => {
    if (!user) return;
    const completedDays = Object.entries(updatedProgress)
      .filter(([, v]) => v)
      .map(([k]) => Number(k));
    localStorage.setItem(PROGRESS_KEY(user.id), JSON.stringify(completedDays));
  }, [user]);

  const handleToggle = useCallback((day: number) => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    const updated = { ...progress, [day]: !progress[day] };
    setProgress(updated);
    saveProgress(updated);
  }, [user, progress, saveProgress]);

  const completedCount = Object.values(progress).filter(Boolean).length;
  const pct = Math.round((completedCount / ANNUAL_PLAN.length) * 100);
  const nextDay = ANNUAL_PLAN.find((d) => !progress[d.day]);
  const displayedDays = showAll ? ANNUAL_PLAN : ANNUAL_PLAN.slice(0, 30);

  const getMonthLabel = (day: number) => {
    const month = Math.ceil(day / 30);
    return month <= 12 ? `Month ${month}` : "Month 12";
  };

  return (
    <div>
      {showLoginPrompt && (
        <SyncCodePromptModal onClose={() => setShowLoginPrompt(false)} />
      )}

      {/* Header */}
      <div className="mb-6">
        <h2 className="text-white text-xl font-bold mb-1">Annual Bible Reading Plan</h2>
        <p className="text-[#555] text-sm">
          Read through the entire Bible in chronological order — 365 days, 3–4 chapters per day
        </p>
      </div>

      {/* Progress card */}
      <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-white text-sm font-semibold mb-0.5">Your Progress</p>
            <p className="text-[#555] text-xs">Full Bible · Chronological Order · 365 days</p>
          </div>
          <span className="text-[#ef4444] text-lg font-bold">{pct}%</span>
        </div>
        <div className="w-full bg-[#1a1a1a] rounded-full h-1.5">
          <div
            className="bg-[#ef4444] h-1.5 rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-[#444] text-xs mt-2">
          {completedCount} of {ANNUAL_PLAN.length} days completed
        </p>

        {!user && (
          <div className="mt-3 flex items-center gap-2 bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2">
            <i className="ri-smartphone-line text-[#666] text-sm" />
            <p className="text-[#555] text-xs">
              <Link to="/settings" className="text-[#ef4444] hover:underline cursor-pointer">
                Sync code
              </Link>{" "}
              to save your reading plan progress
            </p>
          </div>
        )}

        {nextDay && user && (
          <button
            onClick={() => onNavigate(nextDay.chapters[0].bookId, nextDay.chapters[0].chapter)}
            className="mt-3 flex items-center gap-1.5 text-[#ef4444] text-sm cursor-pointer hover:gap-2.5 transition-all whitespace-nowrap"
          >
            Continue Reading: Day {nextDay.day} <i className="ri-arrow-right-line" />
          </button>
        )}
      </div>

      {/* Day list */}
      <div className="space-y-1.5">
        {displayedDays.map((d, idx) => {
          const prevDay = idx > 0 ? displayedDays[idx - 1] : null;
          const showMonthHeader =
            !prevDay || getMonthLabel(d.day) !== getMonthLabel(prevDay.day);

          return (
            <div key={d.day}>
              {showMonthHeader && (
                <p className="text-[#333] text-xs uppercase tracking-widest pt-4 pb-2 px-1">
                  {getMonthLabel(d.day)}
                </p>
              )}
              <div
                className={`flex items-start gap-3 px-4 py-3 rounded-lg border transition-colors ${
                  progress[d.day]
                    ? "bg-[#0f1a0f] border-[#1a3a1a]"
                    : "bg-[#111] border-[#1e1e1e] hover:border-[#2e2e2e] cursor-pointer"
                }`}
                onClick={() => {
                  if (!progress[d.day]) {
                    onNavigate(d.chapters[0].bookId, d.chapters[0].chapter);
                  }
                }}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); handleToggle(d.day); }}
                  className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors cursor-pointer ${
                    progress[d.day]
                      ? "bg-[#22c55e] border-[#22c55e]"
                      : "border-[#333] hover:border-[#ef4444]"
                  }`}
                >
                  {progress[d.day] && (
                    <i className="ri-check-line text-white text-xs" />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <span className="text-[#444] text-xs block mb-0.5">Day {d.day}</span>
                  <div className="flex flex-wrap gap-2">
                    {d.chapters.map((ch) => (
                      <span
                        key={`${ch.bookId}-${ch.chapter}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onNavigate(ch.bookId, ch.chapter);
                        }}
                        className="text-xs text-[#888] hover:text-[#ef4444] transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {ch.bookName} {ch.chapter}
                      </span>
                    ))}
                  </div>
                </div>

                {progress[d.day] && (
                  <span className="text-[#22c55e] text-xs flex-shrink-0 whitespace-nowrap">
                    Read ✓
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!showAll && ANNUAL_PLAN.length > 30 && (
        <button
          onClick={() => setShowAll(true)}
          className="mt-4 w-full py-3 bg-[#111] border border-[#1e1e1e] hover:border-[#2e2e2e] text-[#555] hover:text-white text-sm rounded-xl transition-colors cursor-pointer"
        >
          Show all 365 days
        </button>
      )}
    </div>
  );
}
